import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiClient } from '../../api/apiClient';
import OutgoingInvoices from '../invoices/OutgoingInvoices';
import IncomingInvoices from '../invoices/IncomingInvoices';
import InvoiceTemplate from '../invoices/InvoiceTemplate';
import ReceivedInvoiceTemplate from '../invoices/ReceivedInvoiceTemplate';
import InvoiceForm from '../invoices/InvoiceForm';
import SeriesManagementModal from '../invoices/SeriesManagementModal';

const Invoices = () => {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [activeTab, setActiveTab] = useState('outgoing');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  
  // Invoice Series State
  const [invoiceSeries, setInvoiceSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesFormData, setSeriesFormData] = useState({
    name: '',
    prefix: '',
    startingNumber: 1,
    isActive: true
  });
  const [editingSeries, setEditingSeries] = useState(null);

  const [formData, setFormData] = useState({
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientPhone: '',
    clientTaxIdentifier: '',
    invoiceNumber: '',
    seriesId: null,
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [{ description: '', quantity: 1, price: 0 }],
    notes: '',
    vatRate: 21
  });

  // Fetch invoice series from API
  const fetchInvoiceSeries = useCallback(async () => {
    try {
      const client = getApiClient();
      const response = await client.get('/admin/series');
      // Handle the correct API response structure
      let seriesData = [];
      
      if (response.data && response.data.seriesList && Array.isArray(response.data.seriesList)) {
        seriesData = response.data.seriesList;
      } else if (Array.isArray(response.data)) {
        seriesData = response.data;
      } else {
        console.error('Unexpected API response structure:', response.data);
        setInvoiceSeries([]);
        return;
      }

      const transformedSeries = seriesData.map(series => ({
        id: series.id,
        name: `${series.prefix} Series`,
        prefix: series.prefix,
        startingNumber: 1,
        currentNumber: series.currentNumber,
        documentType: series.documentType,
        isActive: true,
        createdAt: series.creationDate,
        lastUsed: series.modificationDate
      }));

      setInvoiceSeries(transformedSeries);

      // Set default series
      if (transformedSeries.length > 0 && !selectedSeries) {
        const defaultSeries = transformedSeries[0];
        setSelectedSeries(defaultSeries);
        setFormData(prev => ({
          ...prev,
          seriesId: defaultSeries.id,
          invoiceNumber: generateInvoiceNumber(defaultSeries)
        }));
      }
    } catch (error) {
      console.error('Error fetching invoice series:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setInvoiceSeries([]);
    }
  }, [selectedSeries]);

  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    try {
      const client = getApiClient();
      const response = await client.get('/admin/customer/all');

      const transformedClients = response.data.map(customer => ({
        id: customer.customerId,
        name: customer.name,
        email: customer.contact.email,
        phone: customer.contact.phone,
        address: customer.businessData?.address || '',
        taxIdentifier: customer.businessData?.tax_identifier || '',
        regCom: customer.businessData?.reg_com || '',
        city: customer.businessData?.city || '',
        countryCode: customer.businessData?.country_code || 'RO',
        subdivisionCode: customer.businessData?.subdivision_code || '',
        vatPayer: customer.businessData?.vat_payer || false
      }));

      setClients(transformedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchInvoiceSeries();
    fetchClients();
  }, [fetchInvoiceSeries, fetchClients]);

  // Generate invoice number based on series
  const generateInvoiceNumber = (series = selectedSeries) => {
    if (!series) return '';
    
    // Use the exact currentNumber from the series
    const number = String(series.currentNumber).padStart(4, '0');
    
    return `${series.prefix}-${number}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, price: 0 }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  const calculateVAT = () => {
    return calculateSubtotal() * (formData.vatRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  const handleCreateInvoice = async () => {
    try {
      const client = getApiClient();
      
      // Find selected client
      const selectedClient = clients.find(c => c.id === formData.clientId);
      if (!selectedClient) {
        alert('Please select a client');
        return;
      }

      // Prepare invoice data for API
      const invoiceData = {
        seriesId: formData.seriesId,
        issueDate: formData.issueDate,
        seriesNumber: selectedSeries ? `${selectedSeries.prefix}-${selectedSeries.currentNumber}` : formData.invoiceNumber,
        dueDate: formData.dueDate || formData.issueDate,
        type: 'INVOICE_380',
        currency: 'RON',
        buyer: {
          id: selectedClient.id
        },
        lines: formData.items.map((item, index) => ({
          lineNr: index + 1,
          name: item.description,
          identification: `SRV${String(index + 1).padStart(3, '0')}`,
          quantity: item.quantity,
          um: 'H87',
          unitPrice: item.price,
          lineTotal: item.quantity * item.price,
          vatType: 'S',
          vatRate: formData.vatRate,
          vatLine: (item.quantity * item.price) * (formData.vatRate / 100),
          type: 'PRODUCT'
        })),
        direction: 'OUTGOING',
        amount: calculateSubtotal(),
        taxAmount: calculateVAT(),
        totalAmount: calculateTotal(),
      };

      await client.post('/admin/addInvoice', invoiceData);
      // Update series current number locally (increment by 1)
      if (selectedSeries) {
        const updatedSeries = {
          ...selectedSeries,
          currentNumber: selectedSeries.currentNumber + 1,
          lastUsed: new Date().toISOString()
        };
        
        setInvoiceSeries(prev => prev.map(series => 
          series.id === selectedSeries.id ? updatedSeries : series
        ));
        
        setSelectedSeries(updatedSeries);
      }

      // Reset form with new invoice number
      const nextInvoiceNumber = selectedSeries ? generateInvoiceNumber({
        ...selectedSeries,
        currentNumber: selectedSeries.currentNumber + 1
      }) : '';

      setFormData({
        clientId: '',
        clientName: '',
        clientEmail: '',
        clientAddress: '',
        clientPhone: '',
        clientTaxIdentifier: '',
        invoiceNumber: nextInvoiceNumber,
        seriesId: selectedSeries?.id || null,
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        items: [{ description: '', quantity: 1, price: 0 }],
        notes: '',
        vatRate: 21
      });

      // Refresh invoices list
      // Data fetching is now handled by separate components
      // OutgoingInvoices and IncomingInvoices handle their own refresh logic
      setActiveTab('outgoing');
      
      alert('Invoice created successfully!');
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Error creating invoice: ' + (error.response?.data || error.message));
    }
  };

  // Series Management Functions
  const handleCreateSeries = async () => {
    if (!seriesFormData.prefix) {
      alert('Prefix is required');
      return;
    }
    
    try {
      const client = getApiClient();
      
      const seriesData = {
        documentType: 'INVOICE',
        prefix: seriesFormData.prefix,
        currentNumber: seriesFormData.startingNumber
      };

      await client.post('/admin/series/add', seriesData);

      setSeriesFormData({ name: '', prefix: '', startingNumber: 1, isActive: true });
      await fetchInvoiceSeries();
      alert(t('seriesCreated') || 'Series created successfully');
    } catch (error) {
      console.error('Error creating series:', error);
      alert('Error creating series: ' + (error.response?.data?.message || error.response?.data || error.message));
    }
  };

  const handleUpdateSeries = () => {
    // API doesn't provide update endpoint yet
    alert('Update functionality will be implemented when API provides update endpoint');
  };

  const handleDeleteSeries = (seriesId) => {
    // API doesn't provide delete endpoint yet
    alert('Delete functionality will be implemented when API provides delete endpoint');
  };

  const handleToggleSeriesActive = (seriesId) => {
    // API doesn't provide toggle endpoint yet
    alert('Toggle functionality will be implemented when API provides update endpoint');
  };

  // Handle client selection
  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData(prev => ({
        ...prev,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email,
        clientAddress: client.address,
        clientPhone: client.phone,
        clientTaxIdentifier: client.taxIdentifier
      }));
    }
  };

  // Handle series selection
  const handleSeriesSelect = (seriesId) => {
    const series = invoiceSeries.find(s => s.id === seriesId);
    if (series) {
      setSelectedSeries(series);
      setFormData(prev => ({
        ...prev,
        seriesId: series.id,
        invoiceNumber: generateInvoiceNumber(series)
      }));
    }
  };

  // Handle preview invoice from list - now simplified since InvoiceList handles the fetching
  const handlePreviewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: 'RON'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ro-RO');
  };

  const addDefaultService = (service) => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...service }]
    }));
  };

  const handlePreview = () => {
    const invoice = {
      ...formData,
      subtotal: calculateSubtotal(),
      vat: calculateVAT(),
      total: calculateTotal()
    };
    setSelectedInvoice(invoice);
    setShowPreview(true);
  };

  // Remove the getCurrentInvoices function since it's no longer needed

  return (
    <div className="bg-gray-50">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header with Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('outgoing')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'outgoing'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('outgoingInvoices')}
              </button>
              <button
                onClick={() => setActiveTab('incoming')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'incoming'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('incomingInvoices')}
              </button>
              <button
                onClick={() => setActiveTab('generate')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'generate'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('generateInvoice')}
              </button>
              <button
                onClick={() => setShowSeriesModal(true)}
                className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
              >
                {t('invoiceSeries')}
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'outgoing' && (
          <OutgoingInvoices
            onPreviewInvoice={handlePreviewInvoice}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'incoming' && (
          <IncomingInvoices
            onPreviewInvoice={handlePreviewInvoice}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'generate' && (
          <InvoiceForm
            formData={formData}
            clients={clients}
            invoiceSeries={invoiceSeries}
            onInputChange={handleInputChange}
            onItemChange={handleItemChange}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onSeriesSelect={handleSeriesSelect}
            onClientSelect={handleClientSelect}
            onAddDefaultService={addDefaultService}
            calculateSubtotal={calculateSubtotal}
            calculateVAT={calculateVAT}
            calculateTotal={calculateTotal}
            formatCurrency={formatCurrency}
            onPreview={handlePreview}
            onCreateInvoice={handleCreateInvoice}
          />
        )}

        {/* Series Management Modal */}
        <SeriesManagementModal
          isOpen={showSeriesModal}
          onClose={() => setShowSeriesModal(false)}
          invoiceSeries={invoiceSeries}
          seriesFormData={seriesFormData}
          setSeriesFormData={setSeriesFormData}
          editingSeries={editingSeries}
          setEditingSeries={setEditingSeries}
          onCreateSeries={handleCreateSeries}
          onUpdateSeries={handleUpdateSeries}
          onDeleteSeries={handleDeleteSeries}
          onToggleSeriesActive={handleToggleSeriesActive}
          formatDate={formatDate}
        />

        {/* Preview Modal */}
        {showPreview && selectedInvoice && Object.keys(selectedInvoice).length > 0 && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full h-full max-w-none max-h-none rounded-lg shadow-xl flex flex-col">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('previewInvoice')} - {selectedInvoice.invoiceNumber || selectedInvoice.seriesNumber || selectedInvoice.invoiceNr || 'Unknown'}
                </h3>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setSelectedInvoice(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
                <div className="max-w-4xl mx-auto">
                  {selectedInvoice.type === 'incoming' ? (
                    <ReceivedInvoiceTemplate 
                      invoice={selectedInvoice} 
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                    />
                  ) : (
                    <InvoiceTemplate 
                      invoice={selectedInvoice} 
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                    />
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setSelectedInvoice(null);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400"
                >
                  {t('close')}
                </button>
                <button className="px-6 py-2 bg-purple-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-purple-700">
                  {t('downloadPdf')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
